import type { RoleDefinition } from "@/server/types";

const INPUT_CLASS =
  "w-full px-3 py-2 bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-md text-sm text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-blue-500";

const LABEL_CLASS = "block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1";

const MODELS = [
  { value: "sonnet", label: "Sonnet" },
  { value: "opus", label: "Opus" },
  { value: "haiku", label: "Haiku" },
] as const;

type RoleFormProps = {
  role: RoleDefinition;
  onChange: (field: keyof RoleDefinition, value: string | boolean) => void;
  onDelete: () => void;
  onDuplicate: () => void;
};

export const RoleForm = ({ role, onChange, onDelete, onDuplicate }: RoleFormProps) => (
  <div className="space-y-4">
    <div>
      <label className={LABEL_CLASS}>ID</label>
      <input
        type="text"
        value={role.id}
        onChange={(e) => onChange("id", e.target.value)}
        className={`${INPUT_CLASS} font-mono`}
      />
    </div>
    <div>
      <label className={LABEL_CLASS}>Label</label>
      <input
        type="text"
        value={role.label}
        onChange={(e) => onChange("label", e.target.value)}
        className={INPUT_CLASS}
      />
    </div>
    <div>
      <label className={LABEL_CLASS}>Description</label>
      <input
        type="text"
        value={role.description}
        onChange={(e) => onChange("description", e.target.value)}
        className={INPUT_CLASS}
        placeholder="Short description of this role"
      />
    </div>
    <div>
      <label className={LABEL_CLASS}>Default Model</label>
      <select
        value={role.defaultModel}
        onChange={(e) => onChange("defaultModel", e.target.value)}
        className={INPUT_CLASS}
      >
        {MODELS.map((m) => (
          <option key={m.value} value={m.value}>
            {m.label}
          </option>
        ))}
      </select>
    </div>
    <div className="flex items-center gap-4">
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={role.usePlanMode}
          onChange={(e) => onChange("usePlanMode", e.target.checked)}
          className="h-4 w-4 rounded border-zinc-300 text-blue-600 focus:ring-blue-500"
        />
        <span className="text-sm text-zinc-700 dark:text-zinc-300">Use Plan Mode</span>
      </label>
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={role.alwaysVisible ?? false}
          onChange={(e) => onChange("alwaysVisible", e.target.checked)}
          className="h-4 w-4 rounded border-zinc-300 text-blue-600 focus:ring-blue-500"
        />
        <span className="text-sm text-zinc-700 dark:text-zinc-300">Always Visible</span>
      </label>
    </div>
    <div>
      <label className={LABEL_CLASS}>Allowed Tools</label>
      <input
        type="text"
        value={role.allowedTools ?? ""}
        onChange={(e) => onChange("allowedTools", e.target.value)}
        className={`${INPUT_CLASS} font-mono`}
        placeholder={'"Read" "Edit" "Bash(git *)" "Grep" "Glob"'}
      />
      <p className="mt-1 text-xs text-zinc-400">
        Space-separated quoted tool patterns passed to{" "}
        <code className="bg-zinc-100 dark:bg-zinc-800 px-1 rounded">--allowedTools</code>.
        Leave empty for unrestricted access.
      </p>
    </div>
    <div>
      <label className={LABEL_CLASS}>System Prompt</label>
      <textarea
        value={role.systemPrompt ?? ""}
        onChange={(e) => onChange("systemPrompt", e.target.value)}
        rows={4}
        className={`${INPUT_CLASS} resize-y`}
        placeholder="Injected on every Claude invocation via --system-prompt..."
      />
      <p className="mt-1 text-xs text-zinc-400">
        Replaces Claude&apos;s default system prompt on every CLI call.
        Distinct from the Prompt Template below (which wraps each command).
      </p>
    </div>
    <div>
      <label className={LABEL_CLASS}>Prompt Template</label>
      <textarea
        value={role.prompt}
        onChange={(e) => onChange("prompt", e.target.value)}
        rows={8}
        className={`${INPUT_CLASS} resize-y`}
      />
      <p className="mt-1 text-xs text-zinc-400">
        Use <code className="bg-zinc-100 dark:bg-zinc-800 px-1 rounded">{"{{context}}"}</code> as
        placeholder for the message content
      </p>
    </div>
    <div className="pt-4 border-t border-zinc-200 dark:border-zinc-700 flex items-center gap-4">
      <button
        onClick={onDuplicate}
        className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
      >
        Duplicate Role
      </button>
      <button
        onClick={onDelete}
        className="text-sm text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 transition-colors"
      >
        Delete Role
      </button>
    </div>
  </div>
);
